<template>
  <div
    :class="['aiv-select', disabled ? 'disabled' : '']"
    :style="{
      ...customStyle,
    }"
    @keydown="!disabled && handleKeydown($event)"
    @click="handleContainerClick"
  >
    <input
      v-if="searchable"
      type="text"
      v-model="searchQuery"
      @focus="openDropdown"
      @input="filterOptions"
      @click.stop
      :class="['aiv-select-input']"
      :placeholder="searchPlaceholder"
      :id="id ? id : 'select' + JSON.stringify(options && options[0] ? options[0] : {})"
      :disabled="disabled"
      :style="{
        backgroundColor: customStyle?.backgroundColor,
      }"
      :autocomplete="autocomplete || 'on'"
    />
    <span v-else :style="disabled ? { color: '#8a8a8a' } : {}">{{
        multiSelect && selectedOptions.length > 0
          ? selectedOptions.map((option) => option.shortName || option.name).join(", ")
          : selectedOptions[0]?.name || searchQuery || placeholder
      }}</span>
    <span class="icon-container" v-if="!disabled" @click.stop="toggleDropdown">
      <i
        v-if="selectedOptions.length === 0"
        class="fas fa-chevron-down aiv-select-arrow"
        :class="[{ rotate: dropdownOpen }]"
      ></i>
      <i
        v-else-if="clearable"
        class="fas fa-times aiv-select-arrow"
        @click.stop="clearDropdown"
      ></i>
      <i
        v-else
        class="fas fa-chevron-down aiv-select-arrow"
        :class="[{ rotate: dropdownOpen }]"
      ></i>
    </span>
    <portal to="aiv-portals" v-if="dropdownOpen">
      <div
        ref="dropdown"
        class="aiv-select-dropdown scroll"
        :style="dropdownStyle"
        @mousedown.stop
        @wheel.stop
        @mouseleave="handleMouseLeave"
        @mouseenter="handleMouseEnter"
      >
        <div
          v-if="showSelectAll && multiSelect && !checkAllSelected"
          @click="selectAllOptions"
          class="aiv-select-option"
        >
          Select All
        </div>
        <div
          v-for="(option, index) in filteredOptions"
          :key="option.id"
          :class="[
            'aiv-select-option',
            {
              selected: isSelected(option),
              highlighted: index === highlightedIndex,
              disabled: disabled,
            },
          ]"
          @click="!disabled && selectOption(option)"
          @mouseover="!disabled && handleMouseOver(index)"
        >
          {{ option.name }}
          <i v-if="isSelected(option)" class="fas fa-check float-right"></i>
        </div>
        <div
          :class="'aiv-select-option-disabled'"
          v-if="filteredOptions.length === 0"
        >
          None
        </div>
      </div>
    </portal>
  </div>
</template>

<script>
export default {
  name: "AIVSelect",
  props: {
    options: {
      type: Array,
      required: true,
    },
    value: {
      type: [String, Number, Array, Boolean],
      default: null,
    },
    placeholder: {
      type: String,
      default: "Select...",
    },
    placeholderColor: {
      type: String,
    },
    searchable: {
      type: Boolean,
      default: true,
    },
    multiSelect: {
      type: Boolean,
      default: false,
    },
    showSelectAll: {
      type: Boolean,
      default: false,
    },
    hoverClose: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    customStyle: {
      type: Object,
      default: () => ({}),
    },
    id: {
      type: String,
      default: "",
    },
    enableManualInput: {
      type: Boolean,
      default: false,
    },
    autocomplete: {
      type: String,
      default: null,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      searchQuery: "",
      selectedOptions: [],
      dropdownOpen: false,
      searchPlaceholder: this.placeholder,
      highlightedIndex: -1,
      hoverTimeout: null,
      dropdownCoords: { top: 0, left: 0, width: 0 },
    };
  },
  computed: {
    filteredOptions() {
      if (!this.options || !Array.isArray(this.options)) {
        return [];
      }
      if (!this.searchable || !this.searchQuery) {
        return this.options;
      }
      return this.options.filter((option) =>
        option.name.toLowerCase().includes(this.searchQuery.toLowerCase()),
      );
    },
    checkAllSelected() {
      if (!this.options || !Array.isArray(this.options)) {
        return false;
      }
      return this.selectedOptions.length === this.options.length;
    },
    dropdownStyle() {
      return {
        position: "fixed",
        top: this.dropdownCoords.top + "px",
        left: this.dropdownCoords.left + "px",
        width: this.dropdownCoords.width + "px",
        zIndex: 3000,
        maxHeight: "250px",
        overflowY: "auto",
        backgroundColor: "#656565",
        color: "#e0e0e0",
        border: "1px solid #bdbdbd",
        borderRadius: "4px",
      };
    },
  },
  watch: {
    value(newVal) {
      this.setSelectedOptions(newVal);
    },
    options() {
      // Re-evaluate selected options when options array changes
      // This is important for cases where the value is set before options are loaded
      this.setSelectedOptions(this.value);
    },
    searchQuery(newVal) {
      if (this.enableManualInput && !this.selectedOptions.length) {
        this.$emit("input", newVal);
      }
    },
  },
  methods: {
    handleContainerClick() {
      if (!this.disabled) {
        this.toggleDropdown();
      }
    },
    setSelectedOptions(value) {
      if (!this.options || !Array.isArray(this.options)) {
        this.selectedOptions = [];
        this.updatePlaceholder();
        return;
      }
      if (Array.isArray(value)) {
        this.selectedOptions = this.options.filter((option) =>
          value.includes(option.id),
        );
      } else {
        this.selectedOptions = this.options.filter(
          (option) => option.id === value,
        );
      }
      this.updatePlaceholder();
    },
    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen;
      if (this.dropdownOpen) {
        this.openDropdown();
        this.highlightedIndex = -1;
      } else {
        this.closeDropdown();
      }
    },
    openDropdown() {
      this.dropdownOpen = true;
      this.highlightedIndex = -1;
      this.$nextTick(() => {
        this.adjustDropdownPosition();
        window.addEventListener("scroll", this.adjustDropdownPosition, true);
        window.addEventListener("resize", this.adjustDropdownPosition);
      });
    },
    closeDropdown() {
      window.removeEventListener("scroll", this.adjustDropdownPosition, true);
      window.removeEventListener("resize", this.adjustDropdownPosition);
      this.dropdownOpen = false;
      this.highlightedIndex = -1;
    },
    adjustDropdownPosition() {
      const trigger = this.$el.getBoundingClientRect();
      const dropEl = this.$refs.dropdown;

      const h = dropEl ? Math.min(dropEl.scrollHeight, 250) : 250;

      const spaceBelow = window.innerHeight - trigger.bottom;
      const spaceAbove = trigger.top;
      const openUp = spaceBelow < h && spaceAbove > h;

      this.dropdownCoords = {
        top: openUp ? trigger.top - h : trigger.bottom,
        left: trigger.left,
        width: trigger.width,
      };
    },
    filterOptions() {
      this.dropdownOpen = true;
    },
    handleOutsideClick(event) {
      const root = this.$el;
      const drop = this.$refs.dropdown;
      if (
        root.contains(event.target) ||
        (drop && drop.contains(event.target))
      ) {
        return;
      }
      this.closeDropdown();
    },
    handleKeydown(event) {
      switch (event.key) {
        case "Enter":
          if (this.dropdownOpen) {
            if (this.highlightedIndex >= 0) {
              this.selectOption(this.filteredOptions[this.highlightedIndex]);
            }
            this.closeDropdown();
          } else {
            this.openDropdown();
          }
          event.preventDefault();
          break;

        case "ArrowDown":
          if (!this.dropdownOpen) {
            this.openDropdown();
          } else {
            this.highlightedIndex =
              (this.highlightedIndex + 1) % this.filteredOptions.length;
          }
          event.preventDefault();
          break;

        case "ArrowUp":
          if (this.dropdownOpen) {
            this.highlightedIndex =
              (this.highlightedIndex - 1 + this.filteredOptions.length) %
              this.filteredOptions.length;
          }
          event.preventDefault();
          break;

        case "Tab":
          this.closeDropdown();
          break;
      }
    },
    handleMouseOver(index) {
      this.highlightedIndex = index;
    },
    selectOption(option) {
      if (this.multiSelect) {
        if (this.isSelected(option)) {
          this.selectedOptions = this.selectedOptions.filter(
            (selected) => selected.id !== option.id,
          );
        } else {
          this.selectedOptions.push(option);
        }
        // Clear search query so user can search for next person
        this.searchQuery = "";
      } else {
        this.selectedOptions = [option];
        this.searchQuery = "";
        this.closeDropdown();
      }
      this.emitSelection();
      this.$forceUpdate();
      this.updatePlaceholder();
    },
    selectAllOptions() {
      if (!this.options || !Array.isArray(this.options)) {
        return;
      }
      this.selectedOptions = [...this.options];
      this.emitSelection();
    },
    updatePlaceholder() {
      this.searchPlaceholder = this.placeholder;
      let extraCount = 0;

      for (let option of this.selectedOptions) {
        if (this.selectedOptions.indexOf(option) >= 2) {
          extraCount++;
          continue;
        }
        if (this.searchPlaceholder === this.placeholder) {
          this.searchPlaceholder = option.name;
        } else {
          this.searchPlaceholder += ", " + option.name;
        }
      }

      if (extraCount > 0) {
        this.searchPlaceholder += ", +" + extraCount;
      }

      const placeholder = document.getElementById(
        this.id ? this.id : "select" + JSON.stringify(this.options && this.options[0] ? this.options[0] : {}),
      );

      if (placeholder) {
        let placeholderColor = "#9b9b9b";

        this.$nextTick(() => {
          placeholder.style.setProperty(
            "--placeholder-color",
            placeholderColor,
          );
        });
      }
    },
    emitSelection() {
      let emittedValue;
      if (this.selectedOptions.length) {
        emittedValue = this.multiSelect
          ? this.selectedOptions.map((option) => option.id)
          : this.selectedOptions[0]?.id;
      } else if (this.enableManualInput) {
        emittedValue = this.searchQuery;
      } else {
        emittedValue = this.multiSelect ? [] : null;
      }

      this.$emit("input", emittedValue);
      this.$emit("change", emittedValue);
    },
    isSelected(option) {
      return (
        Array.isArray(this.selectedOptions) &&
        this.selectedOptions.some((selected) => selected.id === option.id)
      );
    },
    clearDropdown() {
      this.selectedOptions = [];
      this.searchQuery = "";
      this.emitSelection();
      this.closeDropdown();
      this.updatePlaceholder();
      // Prevent focus from staying on the x button which might cause page background changes
      this.$nextTick(() => {
        if (document.activeElement && document.activeElement.blur) {
          document.activeElement.blur();
        }
      });
    },
    handleMouseEnter() {
      clearTimeout(this.hoverTimeout);
    },
    handleMouseLeave() {
      if (this.hoverClose) {
        this.hoverTimeout = setTimeout(() => {
          this.closeDropdown();
        }, 500);
      }
    },
  },
  mounted() {
    this.setSelectedOptions(this.value);
    document.addEventListener("click", this.handleOutsideClick);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleOutsideClick);
    window.removeEventListener("scroll", this.adjustDropdownPosition, true);
    window.removeEventListener("resize", this.adjustDropdownPosition);
    clearTimeout(this.hoverTimeout);
  },
};
</script>

<style scoped>
.aiv-select {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #656565;
  border: 1px solid #bdbdbd;
  color: #e0e0e0;
  border-radius: 4px;
  height: 40px;
  padding-left: 12px;
  font-size: 14px;
  cursor: pointer;
}

.aiv-select:focus-within {
  border-color: #ffffff !important;
}

.aiv-select-input {
  padding: 0;
  border: none;
  cursor: pointer;
  width: 100%;
  background-color: transparent;
  color: #ffffff;
}

.aiv-select-input::placeholder {
  color: #e0e0e0;
}

.aiv-select-arrow {
  color: #ffffff !important;
  font-size: 14px;
}

.aiv-select-dropdown {
  background-color: #656565;
  border: 1px solid #bdbdbd;
  position: absolute;
  top: 100%;
  left: -1px;
  width: calc(100% + 2px);
  border-radius: 4px;
  max-height: 250px;
  overflow-y: auto;
  overscroll-behavior: contain;
  z-index: 50;
}

.aiv-select-option {
  padding: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #e0e0e0;
}

/*noinspection CssUnusedSymbol */
.aiv-select-option:hover,
.aiv-select-option.highlighted {
  background-color: grey;
}

/*noinspection CssUnusedSymbol */
.aiv-select-option.selected {
  font-weight: bold;
}

/*noinspection CssUnusedSymbol */
.aiv-select.disabled {
  pointer-events: none;
  background-color: #4a4a4a;
  color: #8a8a8a;
  cursor: not-allowed;
  opacity: 0.7;
  border-color: #6a6a6a;
}

.aiv-select-input:disabled {
  background-color: transparent;
  color: #8a8a8a;
  cursor: not-allowed;
}

/*noinspection CssUnusedSymbol */
.aiv-select-option.disabled {
  pointer-events: none;
  color: #a0a0a0;
  cursor: not-allowed;
}

.icon-container {
  padding-right: 10px;
}

.aiv-select-option-disabled {
  padding: 8px;
  cursor: not-allowed;
  color: grey;
}

/*noinspection CssUnusedSymbol */
.aiv-select-arrow.rotate {
  transform: rotate(180deg);
}
</style>
